import { Handle, Position } from "@xyflow/react";
import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { Form, Formik, FormikProps } from "formik";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import { IMeasurementTypeOptions } from "../../../../../../@types/Measurements";
import {
  IWorkflowsSelectOption,
  IWorkflowsSurveyOption,
  IWorkflowResponse,
  IWorkflowArticleOption,
} from "../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../assets/icons/ArrowDown";
import {
  IOption,
  SelectDropdown,
} from "../../../../../../components/SelectDropdown";
import { TextArea } from "../../../../../../components/TextArea";
import { TextField } from "../../../../../../components/TextField";
import { TimePickerInput } from "../../../../../../components/TimePicker";
import { getWorkflowOptionLabel } from "../../../../../../utils/workflowOptionLabels";
import {
  ErrorMessage,
  selectCss,
  textAreaCss,
} from "../../WorkflowsBuilder.styles";
import {
  BottomContainer,
  Container,
  OptionsContainer,
  popupOptionsCss,
  SelectContainer,
  TextFieldCss,
  Title,
  TrueOrFalseBox,
} from "./ResponseComponent.styles";

interface IResponseComponentBaseProps {
  responseTypes?: IWorkflowsSelectOption[];
  responseTimes?: IWorkflowsSelectOption[];
  actionTypes?: IWorkflowsSelectOption[];
  surveyOptions?: IWorkflowsSurveyOption[];
  articleOptions?: IWorkflowArticleOption[];
  measurementTypes?: IMeasurementTypeOptions[];
  hasPatientsWithCarerEmail?: boolean;
}

interface IResponseComponentFormikProps extends IResponseComponentBaseProps {
  content?: IWorkflowResponse;
}

interface IResponseComponentProps extends IResponseComponentBaseProps {
  formik: FormikProps<IWorkflowResponse>;
}

export function ResponseComponent({
  formik,
  responseTypes,
  responseTimes,
  actionTypes,
  surveyOptions,
  articleOptions,
  measurementTypes,
  hasPatientsWithCarerEmail,
}: IResponseComponentProps) {
  const { t } = useTranslation();

  const getMessagePlaceholder = (responseType?: string) => {
    if (responseType === "StaffAlert")
      return t("workflows_response_writeAlert");
    if (responseType === "EmailCarer")
      return t("workflows_response_writeCarerMessage");
    return t("workflows_response_writeMessage");
  };

  const [filteredSurveyOptions, setFilteredSurveyOptions] = useState<
    IWorkflowsSurveyOption[]
  >([]);

  const shouldShowErrorMessage = (
    field:
      | "responseType"
      | "actionType"
      | "measurementType"
      | "surveyId"
      | "responseTime"
      | "timeUtc"
      | "messageText"
      | "questionText"
      | "option1Text"
      | "option2Text"
      | "articleId"
  ) => {
    return formik?.touched[field] ? formik?.errors[field] ?? "" : "";
  };

  const handleMeasurementTypesOptions = () => {
    if (measurementTypes?.length === 0 || !measurementTypes) return [];

    const categoryOrder = ["spirometry", "measurement", "lab_result"];

    const groupedData = measurementTypes?.reduce(
      (accumulator: any, measurementType: IMeasurementTypeOptions) => {
        if (!accumulator[measurementType.category]) {
          accumulator[measurementType.category] = {
            label: measurementType.categoryDisplayName,
            title: measurementType.category,
            options: [],
          };
        }
        accumulator[measurementType.category]?.options?.push({
          label: measurementType.measurementTypeDisplayName,
          value: measurementType.measurementType,
        });
        return accumulator;
      },
      {}
    );

    const orderedData = categoryOrder
      ?.filter((category) => groupedData[category])
      ?.map((category) => {
        groupedData[category].options?.sort((a: IOption, b: IOption) =>
          a.label.localeCompare(b.label)
        );
        return groupedData[category];
      }) as DefaultOptionType[];

    return Object.values(orderedData) ?? [];
  };

  useEffect(() => {
    if (!surveyOptions?.length) return;

    const filteredOptions: IWorkflowsSurveyOption[] = [];

    surveyOptions?.forEach((surveyOption) => {
      const optionIndex = filteredOptions.findIndex(
        (option) => option.surveyId === surveyOption.surveyId
      );
      if (optionIndex === -1) {
        filteredOptions.push(surveyOption);
      }
    });

    setFilteredSurveyOptions(filteredOptions);
  }, [surveyOptions]);

  return (
    <Container>
      <Title>{t("workflows_response_title")}</Title>

      <SelectContainer isError={shouldShowErrorMessage("responseType") !== ""}>
        <SelectDropdown
          placeholder={t("workflows_response_responseType")}
          value={formik.values.responseType}
          options={
            responseTypes?.map((responseType) => {
              return {
                label: getWorkflowOptionLabel("responseType", responseType, t),
                value: responseType.type,
                key: responseType.type,
              };
            }) ?? []
          }
          onValueChange={(value) => {
            formik.setValues({});
            formik.setFieldValue("responseType", value as string);
          }}
          className={`${selectCss()} nopan nodrag`}
        />
        <ErrorMessage>{shouldShowErrorMessage("responseType")}</ErrorMessage>
      </SelectContainer>

      {formik.values.responseType === "OnceOffProtocolAction" ? (
        <SelectContainer isError={shouldShowErrorMessage("actionType") !== ""}>
          <SelectDropdown
            placeholder={t("workflows_response_actionType")}
            value={formik.values.actionType}
            options={
              actionTypes?.map((actionType) => {
                return {
                  label: getWorkflowOptionLabel("actionType", actionType, t),
                  value: actionType.type,
                  key: actionType.type,
                };
              }) ?? []
            }
            onValueChange={(value) => {
              formik.setFieldValue("actionType", value as string);
            }}
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>{shouldShowErrorMessage("actionType")}</ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.actionType === "Measurement" &&
      formik.values.responseType === "OnceOffProtocolAction" ? (
        <SelectContainer
          isError={shouldShowErrorMessage("measurementType") !== ""}
        >
          <Select
            placeholder={t("workflows_response_fvc")}
            defaultValue={formik.values.measurementType}
            suffixIcon={<ArrowDown />}
            options={handleMeasurementTypesOptions()}
            popupClassName={popupOptionsCss()}
            onChange={(value) => formik.setFieldValue("measurementType", value)}
            className={`${selectCss()} nopan nodrag`}
          />

          <ErrorMessage>
            {shouldShowErrorMessage("measurementType")}
          </ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.responseType === "CareInfoArticle" ? (
        <SelectContainer isError={shouldShowErrorMessage("articleId") !== ""}>
          <SelectDropdown
            placeholder={t("workflows_response_article")}
            value={formik.values.articleId}
            options={
              articleOptions?.map((article) => {
                return {
                  label: article.name,
                  value: article.id,
                  key: uuidv4(),
                };
              }) ?? []
            }
            onValueChange={(value) => {
              formik.setFieldValue("articleId", value as string);
            }}
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>{shouldShowErrorMessage("articleId")}</ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.actionType === "Survey" ? (
        <SelectContainer isError={shouldShowErrorMessage("surveyId") !== ""}>
          <SelectDropdown
            placeholder={t("workflows_response_surveyName")}
            value={formik.values.surveyId}
            showSearch
            optionFilterProp="label"
            options={
              filteredSurveyOptions?.map((survey) => {
                return {
                  label: survey.surveyDisplayName,
                  value: survey.surveyId,
                  key: uuidv4(),
                };
              }) ?? []
            }
            onValueChange={(value) => {
              formik.setFieldValue("surveyId", value as string);
            }}
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>{shouldShowErrorMessage("surveyId")}</ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.actionType !== undefined ||
      formik.values.responseType === "FollowUpMessage" ||
      formik.values.responseType === "FollowUpQuestion" ||
      formik.values.responseType === "MessageCareTeam" ||
      formik.values.responseType === "EmailCarer" ? (
        <SelectContainer
          isError={shouldShowErrorMessage("responseTime") !== ""}
        >
          <SelectDropdown
            placeholder={t("workflows_response_responseTime")}
            value={formik.values.responseTime}
            options={
              responseTimes?.map((responseTime) => {
                return {
                  label: getWorkflowOptionLabel(
                    "responseTime",
                    responseTime,
                    t
                  ),
                  value: responseTime.type,
                  key: responseTime.type,
                };
              }) ?? []
            }
            onValueChange={(value) => {
              formik.setFieldValue("responseTime", value as string);
            }}
            className={`${selectCss()} nopan nodrag`}
          />
          <ErrorMessage>{shouldShowErrorMessage("responseTime")}</ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.responseTime === "NextDay" ? (
        <SelectContainer isError={shouldShowErrorMessage("timeUtc") !== ""}>
          <TimePickerInput
            value={formik.values.timeUtc}
            placeholder={t("workflows_response_selectTime")}
            format="HH:mm"
            onChange={(value) => {
              formik.setFieldValue("timeUtc", value);
            }}
          />
          <ErrorMessage>{shouldShowErrorMessage("timeUtc")}</ErrorMessage>
        </SelectContainer>
      ) : null}

      {formik.values.responseType === "FollowUpMessage" ||
      formik.values.responseType === "StaffAlert" ||
      formik.values.responseType === "EmailCarer" ? (
        <SelectContainer>
          <TextArea
            name="messageText"
            backgroudColor="white"
            placeholder={getMessagePlaceholder(formik.values.responseType)}
            maxLength={200}
            value={formik.values.messageText}
            onChange={formik.handleChange}
            className={`${textAreaCss()} nopan nodrag`}
          />
          <ErrorMessage>{shouldShowErrorMessage("messageText")}</ErrorMessage>
          {formik.values.responseType === "EmailCarer" &&
          !hasPatientsWithCarerEmail ? (
            <ErrorMessage
              css={{
                color: "#F1801F !important",
                fontSize: "11px !important",
                marginTop: "4px",
              }}
            >
              {t("workflows_response_noCarerEmail")}
            </ErrorMessage>
          ) : null}
        </SelectContainer>
      ) : null}

      {formik.values.responseType === "FollowUpQuestion" ? (
        <>
          <SelectContainer
            style={{ marginTop: "10px" }}
            isError={shouldShowErrorMessage("questionText") !== ""}
          >
            <p>{t("workflows_response_question")}</p>
            <TextArea
              name="questionText"
              backgroudColor="white"
              placeholder={t("workflows_response_questionPlaceholder")}
              value={formik.values.questionText}
              onChange={formik.handleChange}
            />
            <ErrorMessage>
              {shouldShowErrorMessage("questionText")}
            </ErrorMessage>
          </SelectContainer>

          <SelectContainer style={{ marginTop: "10px" }}>
            <p>{t("workflows_response_options")}</p>
            <OptionsContainer>
              <TextField
                name="option1Text"
                placeholder={t("workflows_response_optionOne")}
                backgroudColor="white"
                value={formik.values.option1Text}
                label=""
                onChange={formik.handleChange}
                className={`${TextFieldCss()} nopan nodrag`}
                errorMessage={shouldShowErrorMessage("option1Text")}
              />
              <TextField
                name="option2Text"
                placeholder={t("workflows_response_optionTwo")}
                backgroudColor="white"
                value={formik.values.option2Text}
                label=""
                onChange={formik.handleChange}
                className={`${TextFieldCss()} nopan nodrag`}
                errorMessage={shouldShowErrorMessage("option2Text")}
              />
            </OptionsContainer>
          </SelectContainer>

          <BottomContainer>
            <TrueOrFalseBox>{t("workflows_response_true")}</TrueOrFalseBox>
            <TrueOrFalseBox isRed>
              {t("workflows_response_false")}
            </TrueOrFalseBox>
          </BottomContainer>
          <Handle
            type="source"
            id="true"
            position={Position.Bottom}
            style={{ left: "86px" }}
          />
          <Handle
            type="source"
            id="false"
            position={Position.Bottom}
            style={{ left: "240px" }}
          />
        </>
      ) : (
        <Handle type="source" id="true" position={Position.Bottom} />
      )}
    </Container>
  );
}

export const ResponseComponentFormik = forwardRef<
  FormikProps<IWorkflowResponse>,
  IResponseComponentFormikProps
>(
  (
    {
      responseTypes,
      responseTimes,
      actionTypes,
      surveyOptions,
      articleOptions,
      measurementTypes,
      hasPatientsWithCarerEmail,
      content,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const handleOnSubmitForm = () => {};

    const responseValidationSchema = yup.object().shape({
      responseType: yup
        .string()
        .required(t("workflows_response_validation_responseTypeRequired")),
      responseTime: yup.string().when("responseType", {
        is: (value: string) =>
          ["CareInfoArticle", "StaffAlert"].includes(value),
        then: yup.string(),
        otherwise: yup
          .string()
          .required(t("workflows_response_validation_responseTimeRequired")),
      }),
      actionType: yup.string().when("responseType", {
        is: "OnceOffProtocolAction",
        then: yup
          .string()
          .required(t("workflows_response_validation_actionTypeRequired")),
        otherwise: yup.string(),
      }),
      measurementType: yup.string().when("actionType", {
        is: "Measurement",
        then: yup
          .string()
          .required(t("workflows_response_validation_measurementTypeRequired")),
        otherwise: yup.string(),
      }),
      surveyId: yup.string().when("actionType", {
        is: "Survey",
        then: yup
          .string()
          .required(t("workflows_response_validation_surveyRequired")),
        otherwise: yup.string(),
      }),
      timeUtc: yup.string().when("responseTime", {
        is: "NextDay",
        then: yup
          .string()
          .required(t("workflows_response_validation_timeUtcRequired")),
        otherwise: yup.string(),
      }),
      messageText: yup.string().when("responseType", {
        is: (value: string) =>
          ["FollowUpMessage", "StaffAlert", "EmailCarer"].includes(value),
        then: yup
          .string()
          .required(t("workflows_response_validation_messageRequired")),
        otherwise: yup.string(),
      }),
      questionText: yup.string().when("responseType", {
        is: "FollowUpQuestion",
        then: yup
          .string()
          .required(t("workflows_response_validation_questionRequired")),
        otherwise: yup.string(),
      }),
      option1Text: yup.string().when("responseType", {
        is: "FollowUpQuestion",
        then: yup
          .string()
          .required(t("workflows_response_validation_required")),
        otherwise: yup.string(),
      }),
      option2Text: yup.string().when("responseType", {
        is: "FollowUpQuestion",
        then: yup
          .string()
          .required(t("workflows_response_validation_required")),
        otherwise: yup.string(),
      }),
      articleId: yup.string().when("responseType", {
        is: "CareInfoArticle",
        then: yup
          .string()
          .required(t("workflows_response_validation_articleRequired")),
        otherwise: yup.string(),
      }),
    });

    return (
      <Formik<IWorkflowResponse>
        innerRef={ref}
        initialValues={{
          responseType: content?.responseType,
          actionType: content?.actionType,
          measurementType: content?.measurementType,
          surveyId: content?.surveyId,
          articleId: content?.articleId,
          responseTime: content?.responseTime,
          timeUtc: content?.timeUtc,
          messageText: content?.messageText,
          questionText: content?.questionText,
          option1Text: content?.option1Text,
          option2Text: content?.option2Text,
        }}
        onSubmit={handleOnSubmitForm}
        validationSchema={responseValidationSchema}
        enableReinitialize
      >
        {(formik) => (
          <Form>
            <ResponseComponent
              formik={formik}
              responseTypes={responseTypes}
              responseTimes={responseTimes}
              actionTypes={actionTypes}
              surveyOptions={surveyOptions}
              articleOptions={articleOptions}
              measurementTypes={measurementTypes}
              hasPatientsWithCarerEmail={hasPatientsWithCarerEmail}
            />
          </Form>
        )}
      </Formik>
    );
  }
);

export function ResponseNode(props: any) {
  const { data } = props;

  return (
    <>
      <Handle id="target" type="target" position={Position.Top} />
      <ResponseComponentFormik
        responseTypes={data.responseTypes}
        responseTimes={data.responseTimes}
        actionTypes={data.actionTypes}
        surveyOptions={data.surveyOptions}
        measurementTypes={data.measurementTypeOptions}
        articleOptions={data.articleOptions}
        hasPatientsWithCarerEmail={data.hasPatientsWithCarerEmail}
        ref={data.ref}
        content={data.content}
      />
    </>
  );
}
